import { useApolloClient } from "@apollo/client";
import {
  PolicyKindEnum,
  PortalPolicyTemplateDocument,
  PortalPolicyTemplateQuery,
  PortalPolicyTemplateQueryVariables,
  usePortalKioskDeactivateMutation,
  usePortalKioskRegisterMutation,
  usePortalManageQuery,
  usePortalPolicyPublishMutation,
  usePortalRoleSettingsSaveMutation,
  usePortalRulesUpdateMutation,
  usePortalShiftSaveMutation,
  usePortalWorkSiteSaveMutation,
} from "@dashboard/graphql";
import {
  Box,
  Button,
  Checkbox,
  Input,
  Option,
  Select,
  Text,
  Textarea,
} from "@saleor/macaw-ui-next";
import { Fragment, ReactNode, useState } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { useAbsenceKindLabel } from "../AbsenceList";
import { ErrorText, Page, Section } from "../components";
import { getKioskToken, setKioskToken } from "../device";
import { portalLabels as l } from "../labels";
import { portalMessages as m } from "../messages";
import { portalPaths } from "../urls";
import { addDays, formatDateTime, today, weekStart } from "../utils";
import {
  Week,
  weekFromRows,
  WeekScheduleEditor,
  WeekSummary,
  weekToRows,
} from "../WeekScheduleEditor";

// Every section shows its values read-only; changes need an explicit
// Edit / New, then Save or Cancel (avoids accidental edits).

const EditButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <Button variant="secondary" size="small" onClick={onClick}>
    {label}
  </Button>
);

const FormButtons = ({
  onSave,
  onCancel,
  disabled,
}: {
  onSave: () => void;
  onCancel: () => void;
  disabled?: boolean;
}) => {
  const intl = useIntl();

  return (
    <Box display="flex" gap={2} justifyContent="flex-end">
      <Button size="small" variant="secondary" onClick={onCancel}>
        {intl.formatMessage(m.cancel)}
      </Button>
      <Button size="small" variant="primary" disabled={disabled} onClick={onSave}>
        {intl.formatMessage(m.save)}
      </Button>
    </Box>
  );
};

const ValueGrid = ({ rows }: { rows: Array<[string, ReactNode]> }) => (
  <Box display="grid" gap={2} __gridTemplateColumns="minmax(180px, 320px) 1fr">
    {rows.map(([label, value]) => (
      <Fragment key={label}>
        <Text size={2} color="default2">
          {label}
        </Text>
        <Text size={3}>{value}</Text>
      </Fragment>
    ))}
  </Box>
);

const Swatch = ({ color }: { color?: string | null }) => (
  <Box __width="16px" __height="16px" borderRadius={2} style={{ background: color || "#eee" }} />
);

// --- shifts --------------------------------------------------------------

const EMPTY_SHIFT = {
  id: "",
  name: "",
  startTime: "09:00",
  endTime: "15:00",
  breakMinutes: "60",
  color: "#dbeafe",
  isActive: true,
};

const ShiftsSection = ({ shifts, onSaved }: { shifts: any[]; onSaved: () => void }) => {
  const intl = useIntl();
  const [form, setForm] = useState<typeof EMPTY_SHIFT | null>(null);
  const [save, opts] = usePortalShiftSaveMutation({
    onCompleted: data => {
      if (!data.shiftSave?.errors.length) {
        setForm(null);
        onSaved();
      }
    },
  });

  return (
    <Section
      title={intl.formatMessage(m.shifts)}
      actions={
        !form && (
          <EditButton label={intl.formatMessage(m.newShift)} onClick={() => setForm(EMPTY_SHIFT)} />
        )
      }
    >
      {shifts.map(shift => (
        <Box key={shift.id} display="flex" gap={2} alignItems="center">
          <Swatch color={shift.color} />
          <Text size={3} __minWidth="140px" color={shift.isActive ? undefined : "default2"}>
            {shift.name}
          </Text>
          <Text size={2}>
            {shift.startTime}–{shift.endTime} · {shift.breakMinutes} min
            {!shift.isActive && ` · ${intl.formatMessage(m.inactive)}`}
          </Text>
          <Box __flex={1} />
          {!form && (
            <Button
              size="small"
              variant="tertiary"
              onClick={() => setForm({ ...shift, breakMinutes: String(shift.breakMinutes) })}
            >
              {intl.formatMessage(m.edit)}
            </Button>
          )}
        </Box>
      ))}
      {form && (
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          padding={3}
          borderRadius={3}
          backgroundColor="default2"
        >
          <Box display="grid" gap={2} __gridTemplateColumns="repeat(auto-fit, minmax(120px, 1fr))">
            <Input
              size="small"
              label={intl.formatMessage(m.name)}
              value={form.name}
              maxLength={80}
              onChange={e => setForm({ ...form, name: e.target.value })}
            />
            <Input
              size="small"
              type="time"
              label={intl.formatMessage(m.start)}
              value={form.startTime}
              onChange={e => setForm({ ...form, startTime: e.target.value })}
            />
            <Input
              size="small"
              type="time"
              label={intl.formatMessage(m.end)}
              value={form.endTime}
              onChange={e => setForm({ ...form, endTime: e.target.value })}
            />
            <Input
              size="small"
              type="number"
              min={0}
              label={intl.formatMessage(m.breakMinutes)}
              value={form.breakMinutes}
              onChange={e => setForm({ ...form, breakMinutes: e.target.value })}
            />
            <Box display="flex" flexDirection="column" gap={1}>
              <Text size={1} color="default2">
                {intl.formatMessage(l.color)}
              </Text>
              <input
                type="color"
                value={form.color || "#dbeafe"}
                onChange={e => setForm({ ...form, color: e.target.value })}
              />
            </Box>
            <Checkbox
              checked={form.isActive}
              onCheckedChange={c => setForm({ ...form, isActive: c === true })}
            >
              <Text size={2}>{intl.formatMessage(l.active)}</Text>
            </Checkbox>
          </Box>
          <ErrorText errors={opts.data?.shiftSave?.errors} />
          <FormButtons
            disabled={!form.name.trim() || opts.loading}
            onCancel={() => setForm(null)}
            onSave={() =>
              save({
                variables: {
                  id: form.id || undefined,
                  input: {
                    name: form.name.trim(),
                    startTime: form.startTime,
                    endTime: form.endTime,
                    breakMinutes: Number(form.breakMinutes) || 0,
                    color: form.color,
                    isActive: form.isActive,
                  },
                },
              })
            }
          />
        </Box>
      )}
    </Section>
  );
};

// --- work sites ----------------------------------------------------------

const EMPTY_SITE = {
  id: "",
  name: "",
  latitude: "",
  longitude: "",
  radiusM: "150",
  allowedIpRanges: "",
  isActive: true,
};

const WorkSitesSection = ({ sites, onSaved }: { sites: any[]; onSaved: () => void }) => {
  const intl = useIntl();
  const [site, setSite] = useState<typeof EMPTY_SITE | null>(null);
  const [saveSite, opts] = usePortalWorkSiteSaveMutation({
    onCompleted: data => {
      if (!data.workSiteSave?.errors.length) {
        setSite(null);
        onSaved();
      }
    },
  });

  return (
    <Section
      title={intl.formatMessage(m.workSites)}
      actions={
        !site && (
          <EditButton
            label={intl.formatMessage(m.newWorkSite)}
            onClick={() => setSite(EMPTY_SITE)}
          />
        )
      }
    >
      {sites.map(s => (
        <Box key={s.id} display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <Text size={3} __minWidth="160px">
            {s.name}
          </Text>
          <Text size={2} color="default2">
            {s.latitude.toFixed(5)}, {s.longitude.toFixed(5)} · {s.radiusM} m
            {s.allowedIpRanges ? ` · IP ${s.allowedIpRanges.replace(/\s+/g, " ")}` : ""}
          </Text>
          <Box __flex={1} />
          {!site && (
            <Button
              size="small"
              variant="tertiary"
              onClick={() =>
                setSite({
                  ...s,
                  latitude: String(s.latitude),
                  longitude: String(s.longitude),
                  radiusM: String(s.radiusM),
                })
              }
            >
              {intl.formatMessage(m.edit)}
            </Button>
          )}
        </Box>
      ))}
      {site && (
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          padding={3}
          borderRadius={3}
          backgroundColor="default2"
        >
          <Box display="grid" gap={2} __gridTemplateColumns="repeat(auto-fit, minmax(150px, 1fr))">
            <Input
              size="small"
              label={intl.formatMessage(m.name)}
              value={site.name}
              onChange={e => setSite({ ...site, name: e.target.value })}
            />
            <Input
              size="small"
              label={intl.formatMessage(l.latitude)}
              value={site.latitude}
              onChange={e => setSite({ ...site, latitude: e.target.value })}
            />
            <Input
              size="small"
              label={intl.formatMessage(l.longitude)}
              value={site.longitude}
              onChange={e => setSite({ ...site, longitude: e.target.value })}
            />
            <Input
              size="small"
              type="number"
              label={intl.formatMessage(m.radius)}
              value={site.radiusM}
              onChange={e => setSite({ ...site, radiusM: e.target.value })}
            />
          </Box>
          <Textarea
            size="small"
            rows={2}
            label={intl.formatMessage(m.ipRanges)}
            value={site.allowedIpRanges}
            onChange={e => setSite({ ...site, allowedIpRanges: e.target.value })}
          />
          <ErrorText errors={opts.data?.workSiteSave?.errors} />
          <FormButtons
            disabled={!site.name.trim() || opts.loading}
            onCancel={() => setSite(null)}
            onSave={() =>
              saveSite({
                variables: {
                  id: site.id || undefined,
                  input: {
                    name: site.name.trim(),
                    latitude: Number(site.latitude),
                    longitude: Number(site.longitude),
                    radiusM: Number(site.radiusM),
                    allowedIpRanges: site.allowedIpRanges,
                    isActive: site.isActive,
                  },
                },
              })
            }
          />
        </Box>
      )}
    </Section>
  );
};

// --- rules ---------------------------------------------------------------

type RuleValues = Record<string, string | boolean>;

const RulesSection = ({
  current,
  onSaved,
}: {
  current: Record<string, unknown> | null | undefined;
  onSaved: () => void;
}) => {
  const intl = useIntl();
  const [rules, setRules] = useState<RuleValues | null>(null);
  const [saveRules, opts] = usePortalRulesUpdateMutation({
    onCompleted: data => {
      if (!data.timekeepingRulesUpdate?.errors.length) {
        setRules(null);
        onSaved();
      }
    },
  });

  if (!current) {
    return null;
  }

  const fields: Array<[string, string, "number" | "time" | "date"]> = [
    ["goLiveDate", intl.formatMessage(m.goLiveDate), "date"],
    ["toleranceMinutes", intl.formatMessage(l.ruleTolerance), "number"],
    ["toleranceMonthlyCapMinutes", intl.formatMessage(l.ruleToleranceCap), "number"],
    ["punctualityPatternDays", intl.formatMessage(l.rulePatternDays), "number"],
    ["lunchStart", intl.formatMessage(l.ruleLunchStart), "time"],
    ["lunchEnd", intl.formatMessage(l.ruleLunchEnd), "time"],
    ["lunchMinMinutes", intl.formatMessage(m.ruleLunchMin), "number"],
    ["lunchMaxMinutes", intl.formatMessage(m.ruleLunchMax), "number"],
    ["longSickLeaveDays", intl.formatMessage(m.longSickLeaveDays), "number"],
    ["weeklyAverageLimitHours", intl.formatMessage(m.ruleWeeklyAverage), "number"],
    ["referencePeriodWeeks", intl.formatMessage(m.ruleReferenceWeeks), "number"],
    ["overtimeAlertPercent", intl.formatMessage(m.ruleOvertimeAlert), "number"],
    ["overtimePremiumPercent", intl.formatMessage(m.ruleOvertimePremium), "number"],
    ["compensatoryRestPercent", intl.formatMessage(m.ruleCompensatoryRest), "number"],
    ["minDailyRestHours", intl.formatMessage(l.ruleMinRest), "number"],
    ["rosterNoticeDays", intl.formatMessage(l.ruleNotice), "number"],
    ["dailyOvertimeLimitHours", intl.formatMessage(l.ruleDailyOvertime), "number"],
    ["annualOvertimeLimitHours", intl.formatMessage(l.ruleAnnualOvertime), "number"],
    ["pinStepSeconds", intl.formatMessage(l.rulePinStep), "number"],
  ];
  const flags: Array<[string, string]> = [
    ["vacationBonusEnabled", intl.formatMessage(m.ruleVacationBonus)],
    ["entryPinEnabled", intl.formatMessage(l.ruleEntryPin)],
    ["lunchPinEnabled", intl.formatMessage(l.ruleLunchPin)],
    ["requireOfficeNetwork", intl.formatMessage(l.ruleOfficeNetwork)],
    ["deviceBinding", intl.formatMessage(l.ruleDeviceBinding)],
    ["kioskOnly", intl.formatMessage(l.ruleKioskOnly)],
    ["timeBankEnabled", intl.formatMessage(l.ruleTimeBank)],
  ];
  const startEditing = () =>
    setRules(
      Object.fromEntries(
        Object.entries(current)
          .filter(([key]) => key !== "__typename" && key !== "defaultSchedule")
          .map(([key, value]) => [
            key,
            typeof value === "boolean" ? value : value === null ? "" : String(value),
          ]),
      ),
    );

  return (
    <Section
      title={intl.formatMessage(m.rules)}
      actions={!rules && <EditButton label={intl.formatMessage(m.edit)} onClick={startEditing} />}
    >
      <Text size={2} color="default2">
        {intl.formatMessage(m.rulesHint)}
      </Text>
      {!rules ? (
        <ValueGrid
          rows={[
            ...fields.map(
              ([key, label]) => [label, String(current[key] ?? "—")] as [string, string],
            ),
            ...flags.map(
              ([key, label]) =>
                [label, intl.formatMessage(current[key] ? m.yes : m.no)] as [string, string],
            ),
          ]}
        />
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          gap={3}
          padding={3}
          borderRadius={3}
          backgroundColor="default2"
        >
          <Box display="grid" gap={2} __gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))">
            {fields.map(([field, label, type]) => (
              <Input
                key={field}
                size="small"
                type={type}
                label={label}
                value={String(rules[field] ?? "")}
                onChange={e => setRules({ ...rules, [field]: e.target.value })}
              />
            ))}
          </Box>
          <Box display="grid" gap={2} __gridTemplateColumns="repeat(auto-fit, minmax(260px, 1fr))">
            {flags.map(([field, label]) => (
              <Checkbox
                key={field}
                checked={rules[field] === true}
                onCheckedChange={c => setRules({ ...rules, [field]: c === true })}
              >
                <Text size={2}>{label}</Text>
              </Checkbox>
            ))}
          </Box>
          <ErrorText errors={opts.data?.timekeepingRulesUpdate?.errors} />
          <FormButtons
            disabled={opts.loading}
            onCancel={() => setRules(null)}
            onSave={() =>
              saveRules({
                variables: {
                  toleranceMinutes: Number(rules.toleranceMinutes),
                  minDailyRestHours: Number(rules.minDailyRestHours),
                  rosterNoticeDays: Number(rules.rosterNoticeDays),
                  annualOvertimeLimitHours: Number(rules.annualOvertimeLimitHours),
                  dailyOvertimeLimitHours: Number(rules.dailyOvertimeLimitHours),
                  timeBankEnabled: rules.timeBankEnabled === true,
                  toleranceMonthlyCapMinutes: Number(rules.toleranceMonthlyCapMinutes),
                  punctualityPatternDays: Number(rules.punctualityPatternDays),
                  entryPinEnabled: rules.entryPinEnabled === true,
                  requireOfficeNetwork: rules.requireOfficeNetwork === true,
                  deviceBinding: rules.deviceBinding === true,
                  kioskOnly: rules.kioskOnly === true,
                  pinStepSeconds: Number(rules.pinStepSeconds),
                  lunchPinEnabled: rules.lunchPinEnabled === true,
                  lunchStart: String(rules.lunchStart),
                  lunchEnd: String(rules.lunchEnd),
                  lunchMinMinutes: Number(rules.lunchMinMinutes),
                  lunchMaxMinutes: Number(rules.lunchMaxMinutes),
                  longSickLeaveDays: Number(rules.longSickLeaveDays),
                  weeklyAverageLimitHours: Number(rules.weeklyAverageLimitHours),
                  referencePeriodWeeks: Number(rules.referencePeriodWeeks),
                  overtimeAlertPercent: Number(rules.overtimeAlertPercent),
                  overtimePremiumPercent: Number(rules.overtimePremiumPercent),
                  compensatoryRestPercent: Number(rules.compensatoryRestPercent),
                  goLiveDate: rules.goLiveDate ? String(rules.goLiveDate) : null,
                  vacationBonusEnabled: rules.vacationBonusEnabled === true,
                },
              })
            }
          />
        </Box>
      )}
    </Section>
  );
};

// --- documents -----------------------------------------------------------

const PoliciesSection = ({ documents, onSaved }: { documents: any[]; onSaved: () => void }) => {
  const intl = useIntl();
  const [policy, setPolicy] = useState<{ kind: string; title: string; body: string } | null>(null);
  const client = useApolloClient();
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [publishPolicy, opts] = usePortalPolicyPublishMutation({
    onCompleted: result => {
      if (!result.policyPublish?.errors.length) {
        setPolicy(null);
        onSaved();
      }
    },
  });
  const options: Option[] = [
    { value: PolicyKindEnum.PRIVACY_NOTICE, label: intl.formatMessage(l.policyPrivacy) },
    { value: PolicyKindEnum.INTERNAL_REGULATION, label: intl.formatMessage(l.policyRegulation) },
    { value: PolicyKindEnum.OPTIONAL_CONSENT, label: intl.formatMessage(l.policyConsent) },
  ];
  const hasTemplate =
    policy?.kind === PolicyKindEnum.PRIVACY_NOTICE ||
    policy?.kind === PolicyKindEnum.INTERNAL_REGULATION;

  return (
    <Section
      title={intl.formatMessage(m.policies)}
      actions={
        !policy && (
          <EditButton
            label={intl.formatMessage(m.newDocument)}
            onClick={() => setPolicy({ kind: PolicyKindEnum.PRIVACY_NOTICE, title: "", body: "" })}
          />
        )
      }
    >
      {documents.map(doc => (
        <Box key={doc.id} as="details">
          <Box as="summary" cursor="pointer">
            <Text size={2}>
              {doc.title} · v{doc.version} · {formatDateTime(doc.publishedAt)}
            </Text>
          </Box>
          {doc.body && (
            <Text size={2} __whiteSpace="pre-wrap">
              {doc.body}
            </Text>
          )}
        </Box>
      ))}
      {policy && (
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          padding={3}
          borderRadius={3}
          backgroundColor="default2"
        >
          <Box display="flex" gap={2} alignItems="flex-end" flexWrap="wrap">
            <Box __minWidth="260px">
              <Select
                size="small"
                label={intl.formatMessage(m.kind)}
                options={options}
                value={options.find(o => o.value === policy.kind) ?? null}
                onChange={option => setPolicy({ ...policy, kind: option.value })}
              />
            </Box>
            {hasTemplate && (
              <Button
                size="small"
                variant="secondary"
                disabled={loadingTemplate}
                onClick={async () => {
                  setLoadingTemplate(true);

                  const result = await client
                    .query<PortalPolicyTemplateQuery, PortalPolicyTemplateQueryVariables>({
                      query: PortalPolicyTemplateDocument,
                      variables: { kind: policy.kind as PolicyKindEnum },
                      fetchPolicy: "network-only",
                    })
                    .finally(() => setLoadingTemplate(false));
                  const body = result.data?.policyTemplate ?? "";

                  setPolicy({
                    ...policy,
                    title:
                      policy.title ||
                      intl.formatMessage(
                        policy.kind === PolicyKindEnum.PRIVACY_NOTICE
                          ? l.policyPrivacy
                          : l.policyRegulation,
                      ),
                    body,
                  });
                }}
              >
                {intl.formatMessage(m.loadTemplate)}
              </Button>
            )}
          </Box>
          {hasTemplate && (
            <Text size={2} color="warning1">
              {intl.formatMessage(m.templateHint)}
            </Text>
          )}
          <Input
            size="small"
            label={intl.formatMessage(m.title)}
            value={policy.title}
            maxLength={256}
            onChange={e => setPolicy({ ...policy, title: e.target.value })}
          />
          <Textarea
            rows={16}
            label={intl.formatMessage(m.text)}
            value={policy.body}
            onChange={e => setPolicy({ ...policy, body: e.target.value })}
          />
          <ErrorText errors={opts.data?.policyPublish?.errors} />
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button size="small" variant="secondary" onClick={() => setPolicy(null)}>
              {intl.formatMessage(m.cancel)}
            </Button>
            <Button
              size="small"
              variant="primary"
              disabled={
                !policy.title.trim() ||
                !policy.body.trim() ||
                policy.body.includes("[MODELO") ||
                opts.loading
              }
              onClick={() =>
                publishPolicy({
                  variables: {
                    kind: policy.kind as PolicyKindEnum,
                    title: policy.title,
                    body: policy.body,
                  },
                })
              }
            >
              {intl.formatMessage(m.publishPolicy)}
            </Button>
          </Box>
        </Box>
      )}
    </Section>
  );
};

// --- roles ---------------------------------------------------------------

const RolesSection = ({ roles, onSaved }: { roles: any[]; onSaved: () => void }) => {
  const intl = useIntl();
  const [draft, setDraft] = useState<Record<
    string,
    { tasksEnabled: boolean; dailyReportRequired: boolean }
  > | null>(null);
  const [saveRole, opts] = usePortalRoleSettingsSaveMutation();

  return (
    <Section
      title={intl.formatMessage(m.rolesTitle)}
      actions={
        !draft && (
          <EditButton
            label={intl.formatMessage(m.edit)}
            onClick={() =>
              setDraft(
                Object.fromEntries(
                  roles.map(role => [
                    role.groupId,
                    {
                      tasksEnabled: role.tasksEnabled,
                      dailyReportRequired: role.dailyReportRequired,
                    },
                  ]),
                ),
              )
            }
          />
        )
      }
    >
      {roles.map(role => {
        const value = draft?.[role.groupId] ?? role;

        return (
          <Box key={role.groupId} display="flex" gap={4} alignItems="center" flexWrap="wrap">
            <Text size={3} __minWidth="200px">
              {role.groupName}
            </Text>
            {draft ? (
              <>
                <Checkbox
                  checked={value.tasksEnabled}
                  onCheckedChange={c =>
                    setDraft({ ...draft, [role.groupId]: { ...value, tasksEnabled: c === true } })
                  }
                >
                  <Text size={2}>{intl.formatMessage(m.roleTasks)}</Text>
                </Checkbox>
                <Checkbox
                  checked={value.dailyReportRequired}
                  onCheckedChange={c =>
                    setDraft({
                      ...draft,
                      [role.groupId]: { ...value, dailyReportRequired: c === true },
                    })
                  }
                >
                  <Text size={2}>{intl.formatMessage(m.roleReport)}</Text>
                </Checkbox>
              </>
            ) : (
              <Text size={2} color="default2">
                {intl.formatMessage(m.roleTasks)}:{" "}
                {intl.formatMessage(value.tasksEnabled ? m.yes : m.no)}
                {" · "}
                {intl.formatMessage(m.roleReport)}:{" "}
                {intl.formatMessage(value.dailyReportRequired ? m.yes : m.no)}
              </Text>
            )}
          </Box>
        );
      })}
      <Text size={1} color="default2">
        {intl.formatMessage(m.rolesHint)}
      </Text>
      {draft && (
        <FormButtons
          disabled={opts.loading}
          onCancel={() => setDraft(null)}
          onSave={async () => {
            for (const role of roles) {
              const value = draft[role.groupId];

              if (
                value.tasksEnabled !== role.tasksEnabled ||
                value.dailyReportRequired !== role.dailyReportRequired
              ) {
                await saveRole({ variables: { groupId: role.groupId, ...value } });
              }
            }
            setDraft(null);
            onSaved();
          }}
        />
      )}
    </Section>
  );
};

// --- kiosks --------------------------------------------------------------

const KiosksSection = ({ kiosks, onSaved }: { kiosks: any[]; onSaved: () => void }) => {
  const intl = useIntl();
  const [kioskName, setKioskName] = useState<string | null>(null);
  const [registerKiosk] = usePortalKioskRegisterMutation();
  const [deactivateKiosk] = usePortalKioskDeactivateMutation({ onCompleted: onSaved });

  return (
    <Section
      title={intl.formatMessage(m.kiosks)}
      actions={
        kioskName === null && (
          <EditButton
            label={intl.formatMessage(m.registerKiosk)}
            onClick={() => setKioskName("")}
          />
        )
      }
    >
      {kiosks.map(kiosk => (
        <Box key={kiosk.id} display="flex" gap={2} alignItems="center">
          <Text size={2} color={kiosk.isActive ? undefined : "default2"}>
            {kiosk.name}
            {kiosk.lastUsedAt ? ` · ${formatDateTime(kiosk.lastUsedAt)}` : ""}
          </Text>
          <Box __flex={1} />
          {kiosk.isActive && (
            <Button
              size="small"
              variant="tertiary"
              onClick={() => {
                if (window.confirm(`${intl.formatMessage(m.deactivate)}: ${kiosk.name}?`)) {
                  deactivateKiosk({ variables: { id: kiosk.id } });
                }
              }}
            >
              {intl.formatMessage(m.deactivate)}
            </Button>
          )}
        </Box>
      ))}
      {kioskName !== null && (
        <Box display="flex" gap={2} alignItems="flex-end" flexWrap="wrap">
          <Input
            size="small"
            label={intl.formatMessage(m.name)}
            value={kioskName}
            onChange={e => setKioskName(e.target.value)}
          />
          <Button size="small" variant="secondary" onClick={() => setKioskName(null)}>
            {intl.formatMessage(m.cancel)}
          </Button>
          <Button
            size="small"
            variant="primary"
            disabled={!kioskName.trim()}
            onClick={async () => {
              const result = await registerKiosk({ variables: { name: kioskName.trim() } });
              const token = result.data?.kioskRegister?.token;

              if (token) {
                setKioskToken(token);
                setKioskName(null);
                onSaved();
              }
            }}
          >
            {intl.formatMessage(m.registerKiosk)}
          </Button>
        </Box>
      )}
      <Box display="flex" gap={2} alignItems="center">
        <Text size={1} color="default2">
          {intl.formatMessage(getKioskToken() ? m.isKiosk : m.kioskHint)}
        </Text>
        {getKioskToken() && (
          <Button size="small" variant="tertiary" onClick={() => setKioskToken(null)}>
            {intl.formatMessage(m.forgetKiosk)}
          </Button>
        )}
      </Box>
    </Section>
  );
};

const NormalHoursSection = ({
  rows,
  onSaved,
}: {
  rows: Array<{ weekday: number; start: string; end: string }>;
  onSaved: () => void;
}) => {
  const intl = useIntl();
  const [week, setWeek] = useState<Week | null>(null);
  const [save, opts] = usePortalRulesUpdateMutation({
    onCompleted: data => {
      if (!data.timekeepingRulesUpdate?.errors.length) {
        setWeek(null);
        onSaved();
      }
    },
  });

  return (
    <Section
      title={intl.formatMessage(m.normalHoursTitle)}
      actions={
        !week && (
          <EditButton
            label={intl.formatMessage(m.edit)}
            onClick={() => setWeek(weekFromRows(rows))}
          />
        )
      }
    >
      <Text size={2} color="default2">
        {intl.formatMessage(m.normalHoursHint)}
      </Text>
      {!week ? (
        <WeekSummary week={weekFromRows(rows)} />
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          padding={3}
          borderRadius={3}
          backgroundColor="default2"
        >
          <WeekScheduleEditor week={week} onChange={setWeek} />
          <ErrorText errors={opts.data?.timekeepingRulesUpdate?.errors} />
          <FormButtons
            disabled={opts.loading}
            onCancel={() => setWeek(null)}
            onSave={() => save({ variables: { defaultSchedule: weekToRows(week) } })}
          />
        </Box>
      )}
    </Section>
  );
};

export const Setup = () => {
  const intl = useIntl();
  const absenceKindLabel = useAbsenceKindLabel();
  const start = addDays(weekStart(today()), 7);
  const { data, refetch } = usePortalManageQuery({
    variables: { start, end: addDays(start, 13) },
    fetchPolicy: "cache-and-network",
  });
  const chain = data?.chainVerification;
  const documents = data?.policyDocuments ?? [];

  if (!data) {
    return null;
  }

  return (
    <Page>
      <NormalHoursSection rows={data?.timekeepingRules?.defaultSchedule ?? []} onSaved={refetch} />
      <ShiftsSection shifts={data?.shifts ?? []} onSaved={refetch} />
      <Box display="flex" gap={2} alignItems="center">
        <Text size={2} color="default2">
          {intl.formatMessage(m.rosterMoved)}
        </Text>
        <Link to={portalPaths.rota}>
          <Button size="small" variant="secondary" as="span">
            {intl.formatMessage(m.shiftsPlannerModule)}
          </Button>
        </Link>
      </Box>
      <WorkSitesSection sites={data?.workSites ?? []} onSaved={refetch} />
      <RulesSection current={data?.timekeepingRules} onSaved={refetch} />
      <PoliciesSection documents={documents} onSaved={refetch} />
      <Section title={intl.formatMessage(m.absenceRulesTitle)}>
        <Box __overflowX="auto">
          <table className="portal-rules">
            <thead>
              <tr>
                <th />
                <th>{intl.formatMessage(m.effectiveWork)}</th>
                <th>{intl.formatMessage(m.companyPaid)}</th>
                <th>{intl.formatMessage(m.mealAllowance)}</th>
              </tr>
            </thead>
            <tbody>
              {(data?.absenceKindRules ?? []).map(rule => (
                <tr key={rule.kind}>
                  <th>{absenceKindLabel(rule.kind)}</th>
                  {[rule.effectiveWork, rule.companyPaid, rule.mealAllowance].map((value, i) => (
                    <td key={i}>{intl.formatMessage(value ? m.yes : m.no)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
        <Text size={1} color="default2">
          {intl.formatMessage(m.absenceRulesHint)}
        </Text>
      </Section>
      <RolesSection roles={data?.roleSettings ?? []} onSaved={refetch} />
      <KiosksSection kiosks={data?.kioskDevices ?? []} onSaved={refetch} />
      {chain && (
        <Section title={intl.formatMessage(m.chain)}>
          <Text size={3} color={chain.ok ? "success1" : "critical1"}>
            {chain.ok
              ? intl.formatMessage(m.chainOk, { count: chain.checked })
              : intl.formatMessage(m.chainBroken, { seq: chain.firstBadSeq, error: chain.error })}
          </Text>
        </Section>
      )}
    </Page>
  );
};
